import { Service } from "../@data";

/** Preço avulso da sobrancelha usado na comunicação do desconto */
export const EYEBROW_STANDALONE_PRICE = 15;

/** Desconto comunicado na oferta (sobrancelha avulsa R$15 → combo +R$10 = 40% off) */
export const EYEBROW_DISCOUNT_PERCENT = 40;

const EYEBROW_SUFFIX_REGEX = /\/\s*SOBRA[MN]CELHA\s*$/i;

export function normalizeServiceName(name: string): string {
  return name
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .replace(/\s+/g, " ")
    .trim();
}

/** Serviço cujo nome termina com "/ SOBRANCELHA" (ou SOBRAMCELHA) */
export function isEyebrowVariantService(name: string): boolean {
  return EYEBROW_SUFFIX_REGEX.test(normalizeServiceName(name));
}

export function getBaseServiceName(name: string): string {
  return normalizeServiceName(name).replace(EYEBROW_SUFFIX_REGEX, "").trim();
}

/** Verifica se `variant` é a versão com sobrancelha de `base` */
export function isEyebrowVariantOf(
  variant: Service | undefined,
  base: Service,
): boolean {
  if (!variant || !isEyebrowVariantService(variant.name)) return false;
  return getBaseServiceName(variant.name) === normalizeServiceName(base.name);
}

export function findEyebrowVariant(
  baseService: Service,
  services: Service[],
): Service | undefined {
  if (isEyebrowVariantService(baseService.name)) return undefined;

  const baseName = normalizeServiceName(baseService.name);

  return services.find((service) => {
    if (!isEyebrowVariantService(service.name)) return false;
    return getBaseServiceName(service.name) === baseName;
  });
}

export interface EyebrowUpsellOffer {
  baseService: Service;
  variantService: Service;
  /** Diferença de preço (normalmente R$10) */
  extraPrice: number;
  discountPercent: number;
  /** Preço final do combo */
  finalPrice: number;
}

export function buildEyebrowUpsellOffer(
  baseService: Service,
  services: Service[],
): EyebrowUpsellOffer | null {
  const variantService = findEyebrowVariant(baseService, services);
  if (!variantService) return null;

  const extraPrice = Math.max(0, variantService.price - baseService.price);

  return {
    baseService,
    variantService,
    extraPrice,
    discountPercent: EYEBROW_DISCOUNT_PERCENT,
    finalPrice: variantService.price,
  };
}

export function getEyebrowUpsellMessage(offer: EyebrowUpsellOffer): string {
  const { baseService, discountPercent, finalPrice } = offer;
  const priceLabel = new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(finalPrice);

  return `${baseService.name} + sobrancelha (${discountPercent}% de desconto) por apenas ${priceLabel}. Aproveite!`;
}
